import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactElement } from 'react';

const ProgressBar = ({ value = 0, color = null }: any): ReactElement => {
  const isColor = typeof color === 'function' ? color() : color;

  return (
    <Box sx={{ minWidth: 129, position: 'relative' }}>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 18,
          borderRadius: '20px',
          backgroundColor: 'lightgray',
          '& .MuiLinearProgress-bar': {
            borderRadius: '20px',
            backgroundColor: isColor?.background ?? '#DC2626',
          },
        }}
      />
      <Typography
        sx={{
          position: 'absolute',
          width: '100%',
          textAlign: 'center',
          top: 0,
          lineHeight: '18px',
          fontWeight: 700,
          color: isColor?.color ?? '#FEF2F2',
        }}
      >
        {value} %
      </Typography>
    </Box>
  );
};

const SectionBreakdown = ({ overviewData }: any): ReactElement => (
  <Card variant="outlined">
    <CardHeader
      title={
        <Typography variant="h5" fontWeight={700}>
          Section Breakdown
        </Typography>
      }
      subheader={
        <Typography fontSize={16} color="#7E7E7E" fontWeight={500} mt={3}>
          Maturity assessment by NIST AI RMF function area
        </Typography>
      }
    />
    <CardContent sx={{ p: 4 }}>
      <TableContainer>
        <Table
          size="small"
          sx={{ borderCollapse: 'separate', borderSpacing: 0 }}
        >
          <TableHead>
            <TableRow>
              {[
                'Section',
                'NIST Functions',
                'Questions',
                'Answered',
                'Score',
                'Max',
                'Score %',
                'Maturity Level',
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    height: '48px',
                    color: '#7E7E7E',
                    fontWeight: 500,
                    fontSize: 15,
                    border: '1px solid #E7E7E7',
                    borderBottom: '1px solid #E7E7E7',
                    '&:not(:first-of-type):not(:last-of-type)': {
                      borderLeft: 'none',
                      borderRight: 'none',
                    },
                    '&:first-of-type': {
                      borderRadius: '10px 0 0 0',
                      borderRight: 'none',
                    },
                    '&:last-of-type': {
                      borderRadius: '0 10px 0 0',
                      borderLeft: 'none',
                    },
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {overviewData?.sectionBreakdown?.map((s: any, i: any) => (
              <TableRow
                key={s.sectionTitle}
                hover
                sx={{
                  '& td': {
                    background: i % 2 === 0 ? 'transparent' : '#f4f6fb',
                  },
                  height: '48px',
                }}
              >
                <TableCell sx={{ fontWeight: 600, fontSize: 15 }}>
                  {s?.sectionTitle}
                </TableCell>
                <TableCell sx={{ color: '#777', fontSize: 15 }}>
                  {s?.nistFunctions?.join(', ')}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 15 }}>
                  {s?.questions}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 15 }}>
                  {s?.answered}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 15 }}>
                  {s?.score}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 15 }}>
                  {s?.maxScore}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ProgressBar
                      value={s?.scorePercent}
                      color={() => {
                        if (s?.scorePercent < 20) {
                          return {
                            background: '#FEF2F2',
                            color: '#DC2626',
                          };
                        }
                        if (s?.scorePercent < 40) {
                          return {
                            background: '#FFF7ED',
                            color: '#EA580C',
                          };
                        }
                        if (s?.scorePercent < 60) {
                          return {
                            background: '#FEFCE8',
                            color: '#CA8A04',
                          };
                        }
                        if (s?.scorePercent < 80) {
                          return {
                            background: '#EFF6FF',
                            color: '#2563EB',
                          };
                        }
                        return {
                          background: '#F0FDF4',
                          color: '#16A34A',
                        };
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    sx={() => {
                      if (s?.scorePercent < 20) {
                        return {
                          background: '#FEF2F2',
                          color: '#DC2626',
                          fontWeight: 600,
                        };
                      }
                      if (s?.scorePercent < 40) {
                        return {
                          background: '#FFF7ED',
                          color: '#EA580C',
                          fontWeight: 600,
                        };
                      }
                      if (s?.scorePercent < 60) {
                        return {
                          background: '#FEFCE8',
                          color: '#CA8A04',
                          fontWeight: 600,
                        };
                      }
                      if (s?.scorePercent < 80) {
                        return {
                          background: '#EFF6FF',
                          color: '#2563EB',
                          fontWeight: 600,
                        };
                      }
                      return {
                        background: '#F0FDF4',
                        color: '#16A34A',
                        fontWeight: 600,
                      };
                    }}
                    label={s?.maturityLevel || '-'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
);

export default SectionBreakdown;
